import { useState, useEffect } from 'react';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { loadStripe } from '@stripe/stripe-js';
import { auth, googleProvider } from './firebase';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://us-central1-datasci-playground-dev-3180.cloudfunctions.net/courses-api';
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder';

// Inicializar Stripe
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

function App() {
  const [user, setUser] = useState(null);
  const [courses, setCourses] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCart, setShowCart] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success' ou 'cancel'

  useEffect(() => {
    // Verificar status de pagamento na URL
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      setPaymentStatus('success');
      // Limpar carrinho após pagamento bem-sucedido
      setCart([]);
      localStorage.removeItem('cart');
    } else if (window.location.pathname.includes('cancel')) {
      setPaymentStatus('cancel');
    }

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        fetchCourses();
        // Carregar carrinho do localStorage
        const savedCart = localStorage.getItem('cart');
        if (savedCart && !sessionId) {
          try {
            setCart(JSON.parse(savedCart));
          } catch (e) {
            console.error('Erro ao carregar carrinho:', e);
          }
        }
      } else {
        setCart([]);
        localStorage.removeItem('cart');
      }
    });

    return () => unsubscribe();
  }, []);

  // Salvar carrinho no localStorage sempre que mudar
  useEffect(() => {
    if (user && cart.length > 0) {
      localStorage.setItem('cart', JSON.stringify(cart));
    }
  }, [cart, user]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/courses`);
      const data = await response.json();
      if (data.success) {
        setCourses(data.courses);
      } else {
        setError('Erro ao carregar cursos');
      }
    } catch (err) {
      setError('Erro ao conectar com o servidor');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (course) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.courseId === course.id);
      if (existingItem) {
        return prevCart.map(item =>
          item.courseId === course.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevCart, { courseId: course.id, course: course, quantity: 1 }];
    });
  };

  const removeFromCart = (courseId) => {
    setCart(prevCart => prevCart.filter(item => item.courseId !== courseId));
  };

  const updateCartQuantity = (courseId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(courseId);
      return;
    }
    setCart(prevCart =>
      prevCart.map(item =>
        item.courseId === courseId
          ? { ...item, quantity }
          : item
      )
    );
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.course.price * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      setError('Carrinho vazio');
      return;
    }

    try {
      setCheckoutLoading(true);
      setError(null);

      // Preparar items para o backend
      const items = cart.map(item => ({
        courseId: item.courseId,
        quantity: item.quantity
      }));

      // Criar sessão de checkout
      const response = await fetch(`${API_URL}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      const data = await response.json();

      if (data.success && data.url) {
        // Redirecionar para Stripe Checkout
        window.location.href = data.url;
      } else {
        setError(data.error || 'Erro ao criar sessão de checkout');
      }
    } catch (err) {
      setError('Erro ao processar checkout');
      console.error(err);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error('Erro ao fazer login:', error);
      setError('Erro ao fazer login com Google');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setCourses([]);
      setCart([]);
      localStorage.removeItem('cart');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  // Página de sucesso do pagamento
  if (paymentStatus === 'success') {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    return (
      <div className="app">
        <div className="container">
          <div className="payment-status-page">
            <div className="success-icon">✓</div>
            <h1>Pagamento Realizado com Sucesso!</h1>
            <p>Obrigado por sua compra. Você receberá um email de confirmação em breve.</p>
            {sessionId && (
              <p className="session-id">ID da Sessão: {sessionId}</p>
            )}
            <button onClick={() => { setPaymentStatus(null); window.history.pushState({}, '', '/'); }} className="btn btn-primary">
              Voltar para Cursos
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Página de cancelamento do pagamento
  if (paymentStatus === 'cancel') {
    return (
      <div className="app">
        <div className="container">
          <div className="payment-status-page">
            <div className="cancel-icon">✕</div>
            <h1>Pagamento Cancelado</h1>
            <p>Seu pagamento foi cancelado. Nenhuma cobrança foi realizada.</p>
            <button onClick={() => { setPaymentStatus(null); window.history.pushState({}, '', '/'); }} className="btn btn-primary">
              Voltar para Cursos
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (user) {
    return (
      <div className="app">
        <div className="container">
          <header className="header">
            <h1>Área Logada</h1>
            <div className="user-info">
              <p>Olá, <strong>{user.displayName || user.email}</strong>!</p>
              <div className="header-actions">
                <button 
                  onClick={() => setShowCart(!showCart)} 
                  className="btn btn-cart"
                >
                  🛒 Carrinho ({getCartItemCount()})
                </button>
                <button onClick={handleSignOut} className="btn btn-secondary">
                  Sair
                </button>
              </div>
            </div>
          </header>

          {showCart && (
            <div className="cart-sidebar">
              <div className="cart-header">
                <h2>Carrinho</h2>
                <button onClick={() => setShowCart(false)} className="btn-close">×</button>
              </div>
              <div className="cart-items">
                {cart.length === 0 ? (
                  <p className="cart-empty">Carrinho vazio</p>
                ) : (
                  <>
                    {cart.map((item) => (
                      <div key={item.courseId} className="cart-item">
                        <div className="cart-item-info">
                          <h4>{item.course.name}</h4>
                          <p className="cart-item-price">R$ {item.course.price.toFixed(2)}</p>
                        </div>
                        <div className="cart-item-controls">
                          <button
                            onClick={() => updateCartQuantity(item.courseId, item.quantity - 1)}
                            className="btn-quantity"
                          >
                            −
                          </button>
                          <span className="quantity">{item.quantity}</span>
                          <button
                            onClick={() => updateCartQuantity(item.courseId, item.quantity + 1)}
                            className="btn-quantity"
                          >
                            +
                          </button>
                          <button
                            onClick={() => removeFromCart(item.courseId)}
                            className="btn-remove"
                          >
                            Remover
                          </button>
                        </div>
                      </div>
                    ))}
                    <div className="cart-total">
                      <strong>Total: R$ {getCartTotal().toFixed(2)}</strong>
                    </div>
                    <button
                      onClick={handleCheckout}
                      className="btn btn-primary btn-checkout"
                      disabled={checkoutLoading}
                    >
                      {checkoutLoading ? 'Processando...' : 'Finalizar Compra'}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          <main className="main-content">
            <h2>Cursos Disponíveis</h2>
            
            {loading && <p className="loading">Carregando cursos...</p>}
            {error && <p className="error">{error}</p>}
            
            {!loading && !error && courses.length === 0 && (
              <p>Nenhum curso disponível no momento.</p>
            )}

            <div className="courses-grid">
              {courses.map((course) => {
                const cartItem = cart.find(item => item.courseId === course.id);
                const inCart = !!cartItem;
                
                return (
                  <div key={course.id} className="course-card">
                    <h3>{course.name}</h3>
                    <p className="price">R$ {course.price.toFixed(2)}</p>
                    {inCart ? (
                      <div className="course-in-cart">
                        <span>No carrinho ({cartItem.quantity})</span>
                        <button
                          onClick={() => addToCart(course)}
                          className="btn btn-primary btn-sm"
                        >
                          Adicionar mais
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => addToCart(course)}
                        className="btn btn-primary"
                      >
                        Adicionar ao Carrinho
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="container">
        <header className="header">
          <h1>Cursos de IA</h1>
          <p className="subtitle">Aprenda as tecnologias mais avançadas em Inteligência Artificial</p>
        </header>

        <main className="main-content">
          <div className="hero">
            <h2>Transforme sua carreira com nossos cursos</h2>
            <p>
              Descubra como usar IDEs como copilotos e MCPs para automação. 
              Aprenda com os melhores especialistas do mercado.
            </p>
            
            <div className="features">
              <div className="feature">
                <h3>IDEs como Copilotos</h3>
                <p>Aprenda a usar ferramentas de IA integradas em IDEs para aumentar sua produtividade</p>
              </div>
              <div className="feature">
                <h3>MCPs para Automação</h3>
                <p>Domine Model Context Protocols para criar automações inteligentes</p>
              </div>
            </div>

            <div className="cta-section">
              <p className="cta-text">Faça login para ver os cursos disponíveis e preços</p>
              <button onClick={handleGoogleSignIn} className="btn btn-primary btn-large">
                Entrar com Google
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default App;
